import {
  ArrayField,
  FormDataModel,
  FormField,
  GroupField,
  SectionField,
} from "../data-model/pharmaPaFormModel";

/**
 * Updates the key of a field to include the array index
 */
const updateFieldKey = (
  field: FormField,
  parentKey: string,
  index: number,
): FormField => {
  const elementKey = field.key.split(".").pop() || field.key;
  return {
    ...field,
    key: `${parentKey}.[${index}].${elementKey}`,
  };
};

/**
 * Creates a new array field instance for a specific index
 */
const createArrayFieldInstance = (
  arrayField: ArrayField,
  index: number,
): FormField[] => {
  if (Array.isArray(arrayField.fields) && arrayField.fields.length > 0) {
    const firstField = arrayField.fields[0];
    if (Array.isArray(firstField)) {
      return firstField.map((field) =>
        updateFieldKey(field, arrayField.parentKey || arrayField.key, index),
      );
    } else {
      return (arrayField.fields as unknown as FormField[]).map((field) =>
        updateFieldKey(field, arrayField.parentKey || arrayField.key, index),
      );
    }
  }

  return [];
};

/**
 * Handles array type fields by creating multiple instances based on data length
 */
const handleArrayField = (field: ArrayField, data: any): ArrayField => {
  const arrayLength = data?.[field.parentKey || field.key]?.length || 1;
  const instances = Array.from({ length: arrayLength }, (_, index) =>
    createArrayFieldInstance(field, index),
  );

  return {
    ...field,
    fields: instances,
  };
};

/**
 * Handles nested fields in sections and groups
 */
const handleNestedField = (
  field: SectionField | GroupField,
  data: any,
): FormField => {
  return {
    ...field,
    fields: traverseFields(field.fields, data),
  };
};

/**
 * Traverses through form fields and processes them based on their type
 */
const traverseFields = (fields: FormField[], data: any): FormField[] => {
  return fields.map((field) => {
    if (field.type === "array") {
      return handleArrayField(field as ArrayField, data);
    }

    if (
      (field.type === "section" || field.type === "group") &&
      "fields" in field
    ) {
      return handleNestedField(field as SectionField | GroupField, data);
    }

    return field;
  });
};

/**
 * Main function to replace array field keys with correct indices
 */
export const replaceKeyOfArrayTypeFieldWithCorrectIndex = (
  formConfig: FormDataModel,
  data?: any,
): FormDataModel => {
  if (!formConfig.fields) {
    return formConfig;
  }

  return {
    ...formConfig,
    fields: traverseFields(formConfig.fields, data ?? {}),
  };
};
